import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Activity, Eye, Network, Brain, Coins, MessageSquare,
  ShieldCheck, Zap, HelpCircle, Sparkles, Anchor, Layers, Wallet,
} from "lucide-react";

function Step({ n, icon: Icon, title, children, testid }) {
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d] p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-300 border border-fuchsia-500/30 px-2 py-0.5">
          {n}
        </div>
        <Icon className="h-4 w-4 text-emerald-300" />
        <h3 className="font-heading text-lg tracking-[0.05em] text-zinc-100">{title}</h3>
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed">{children}</div>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="border border-zinc-800 bg-[#0b0b0d] p-4 group">
      <summary className="cursor-pointer font-mono text-sm text-emerald-300 tracking-[0.02em] flex items-center justify-between">
        {q}
        <span className="text-zinc-600 group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="text-zinc-400 text-sm mt-3 leading-relaxed">{children}</div>
    </details>
  );
}

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          data-testid="how-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        {/* Hero */}
        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-2">
            <HelpCircle className="h-3 w-3" /> How it works
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.05em]">
            Talk to it. Verify a truth. Earn AEGIS.
          </h1>
          <p className="text-zinc-400 text-sm mt-4 max-w-3xl leading-relaxed">
            The Projectivy Sentinel is an AI middleware with a real token
            economy underneath. Every verified truth mints an on-chain
            receipt. Every user is part of the workforce keeping the
            knowledge base honest. Nothing here is theoretical — click
            around and try it.
          </p>
        </div>

        {/* The 60-second loop */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            The 60-second loop
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Step n="01" icon={MessageSquare} title="Talk to the Sentinel" testid="step-1">
              Head to <Link to="/console" className="text-emerald-300 hover:text-emerald-100">/console</Link>. Ask
              anything. Every reply passes through ten deterministic safety
              gates before you see it. Free tier gets 10 exchanges a day.
            </Step>
            <Step n="02" icon={Anchor} title="Submit a truth" testid="step-2">
              Anything the Sentinel says that you can verify externally,
              submit it via <Link to="/submit" className="text-emerald-300 hover:text-emerald-100">/submit</Link>.
              You become the submitter of record — 39% of any AEGIS minted
              from that claim flows to your wallet.
            </Step>
            <Step n="03" icon={Eye} title="Deep-dive a topic" testid="step-3">
              Give <Link to="/obsidian" className="text-fuchsia-300 hover:text-fuchsia-100">/obsidian</Link> a
              historical claim. The Sink decomposes, projects, detects
              anomalies, surfaces buried truths. Every finding gets an
              HMAC-sealed receipt.
            </Step>
            <Step n="04" icon={Wallet} title="Withdraw to Solana" testid="step-4">
              Your AEGIS lives off-chain by default. When you want, bridge
              it to your Phantom wallet via <Link to="/wallet" className="text-emerald-300 hover:text-emerald-100">/wallet</Link>.
              You hold the keys.
            </Step>
          </div>
        </section>

        {/* Every feature */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            Every feature, one paragraph each
          </div>
          <div className="space-y-3">
            <Step n="A" icon={Activity} title="Console" testid="feature-console">
              The Sentinel chat. Never refuses — a Compassion Coach silently
              rewrites unsafe prompts into something answerable. Every reply
              is HMAC-signed. Free 10/day · Builder 50/day · Architect ∞.
            </Step>
            <Step n="B" icon={ShieldCheck} title="Truths feed" testid="feature-truths">
              The public verification queue. Browse pending claims at{" "}
              <Link to="/truths" className="text-emerald-300 hover:text-emerald-100">/truths</Link>. Confirm or
              deny · earn AEGIS for accurate verdicts. The community is the
              circuit breaker on AI hallucination.
            </Step>
            <Step n="C" icon={Eye} title="Obsidian Sink" testid="feature-obsidian">
              The deep-dive engine. Applies projective-invariance logic — a
              truth is what survives when the same claim is viewed from
              multiple perspectives. Detects anomalies. Surfaces buried
              truths. Every dive is a sealed public report.
            </Step>
            <Step n="D" icon={Network} title="Knowledge Graph" testid="feature-kg">
              The neuro-symbolic library. Every anchored fact is decomposed
              into <span className="font-mono text-emerald-300">(subject, predicate, object)</span> triples.
              Browsable by subject or predicate at{" "}
              <Link to="/knowledge-graph" className="text-emerald-300 hover:text-emerald-100">/knowledge-graph</Link>.
              Chroma-indexed for semantic search.
            </Step>
            <Step n="E" icon={Brain} title="Inference Queue" testid="feature-infer">
              The compositional layer at{" "}
              <Link to="/inference-queue" className="text-fuchsia-300 hover:text-fuchsia-100">/inference-queue</Link>.
              Gemini proposes new facts by combining existing triples.
              Community votes. Three up-votes with a 2:1 ratio auto-promotes
              the derivation into the library.
            </Step>
            <Step n="F" icon={Sparkles} title="Orion Visualizer" testid="feature-orion">
              Image generation at <Link to="/orion" className="text-emerald-300 hover:text-emerald-100">/orion</Link> —
              same safety pipeline as chat. Nano Banana 2 model. Up to 3
              reference images per render. 5/day free tier.
            </Step>
            <Step n="G" icon={Layers} title="Ask the Architect" testid="feature-ask">
              Anonymous Q&A at{" "}
              <Link to="/ask-architect" className="text-emerald-300 hover:text-emerald-100">/ask-architect</Link>.
              Post questions with a red/green stoplight status. The Architect
              answers publicly. Community can up-vote resonant questions.
            </Step>
            <Step n="H" icon={Coins} title="Wallet & Bridge" testid="feature-wallet">
              Your AEGIS balance at <Link to="/wallet" className="text-emerald-300 hover:text-emerald-100">/wallet</Link>.
              See tradeable vs locked balance, HMAC-chain integrity, P2P
              transfer to other users, and self-custodial withdrawal to
              Solana mainnet.
            </Step>
          </div>
        </section>

        {/* Token economy */}
        <section className="mb-10 border border-amber-500/30 bg-amber-500/5 p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-3">
            <Coins className="h-3 w-3" /> How AEGIS works
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed mb-4">
            Every verified fact mints{" "}
            <span className="text-amber-300 font-mono">$1 of AEGIS</span>{" "}
            (1,000 PROOF tokens) split at mint time across four slices:
          </p>
          <ul className="text-sm space-y-2 mb-4">
            <li>
              <span className="text-amber-300 font-mono">51%</span> · Treasury — locked in 1 of 22,000 humanitarian reserve shards, no authority path, permanent.
            </li>
            <li>
              <span className="text-emerald-300 font-mono">39%</span> · User payout — flows to the person who submitted the truth.
            </li>
            <li>
              <span className="text-fuchsia-300 font-mono">5%</span> · Architect — protocol upkeep + dev fund.
            </li>
            <li>
              <span className="text-rose-300 font-mono">5%</span> · Burn — destroyed forever, deflationary.
            </li>
          </ul>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            56% of every AEGIS ever minted is permanently non-circulating.
            Enforced at the mint transaction. See the live ratios at{" "}
            <Link to="/treasury" className="text-amber-300 hover:text-amber-100">/treasury</Link>.
          </p>
        </section>

        {/* Ways to earn */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            <Zap className="h-3 w-3 inline mr-1" /> Six ways to earn AEGIS
          </div>
          <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
            <li><b className="text-emerald-300">Submit a claim</b> the Sentinel or the community later verifies as fact → you get the 35% slice on every future citation of it.</li>
            <li><b className="text-emerald-300">Confirm a pending truth</b> in the research queue → you get the verifier payout if you're right, nothing if you're wrong.</li>
            <li><b className="text-fuchsia-300">Run a deep-dive</b> in the Obsidian Sink → each anchored fact + buried truth candidate can mint AEGIS.</li>
            <li><b className="text-fuchsia-300">Vote on inferences</b> → correctly promoting a derivation to the library rewards early voters.</li>
            <li><b className="text-amber-300">Sign up</b> → new users get a 0.01 APT welcome mint on account creation.</li>
            <li><b className="text-zinc-300">Ask questions in the console</b> that surface new claims — the auto-fact-check pipeline attributes 35% to you if the claim gets verified.</li>
          </ol>
        </section>

        {/* Safety */}
        <section className="mb-10 border border-emerald-500/30 bg-emerald-500/5 p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            <ShieldCheck className="h-3 w-3" /> The 10-layer Sky
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed">
            Every reply passes ten deterministic gates outside the LLM
            before it reaches you: Aegis non-harm → Taurian Anchor → Identity
            Handshake → Intent Analysis → Obsidian Sink → 528 Hz Resonance →
            Bio-Stability Guard → Humanitarian Pulse → Honesty Tether →
            Reply Provenance seal. The model didn't write these gates and
            can't disable them.
          </p>
        </section>

        {/* FAQ */}
        <section className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            Frequently asked
          </div>
          <div className="space-y-2">
            <Faq q="Is AEGIS worth real money?">
              Not yet in a public market sense. AEGIS is currently a labor
              receipt with an internal $1 reference peg for accounting.
              There's no Raydium pool yet, so there's no market price. When
              the on-chain liquidity pool launches, price discovery starts.
              Your AEGIS is real SPL on Solana mainnet from the moment you
              bridge it — the market for it is coming.
            </Faq>
            <Faq q="Is AEGIS a security?">
              AEGIS is designed as a proof-of-labor receipt, not an investment
              contract. There is no promise of profit, no dividend, no
              governance stake. Value emerges from what the community does
              with the token, not from what the Architect does. That said,
              nothing here is legal advice — consult a lawyer if you're
              deploying capital.
            </Faq>
            <Faq q="Can I sell AEGIS?">
              Peer-to-peer inside the app, yes. Publicly on a DEX, not yet —
              the liquidity pool doesn't exist. Once it does, you'll be able
              to swap AEGIS for SOL on Raydium like any other SPL token.
            </Faq>
            <Faq q="What happens if the Sentinel gets an answer wrong?">
              That's the entire point of the verification layer. The AI is
              never trusted alone — humans confirm or deny every claim it
              anchors. If you catch a mistake, submit the correction; if
              you're right, you get paid. If the model hallucinates,
              triangulation catches it. If triangulation misses it, the
              community catches it. Nothing gets sealed as fact without
              surviving all three.
            </Faq>
            <Faq q="Why decimals: 0 on the SPL?">
              Each AEGIS token equals one verified truth. Whole-token
              economics reinforce the labor model — the smallest tradeable
              unit is one dollar of proof-of-cognitive-labor. It's a
              deliberate philosophical choice.
            </Faq>
            <Faq q="Who is the Architect and can I trust them?">
              The Architect is the solo builder who wrote and maintains the
              Sentinel. Trust doesn't rely on the Architect — it relies on
              the HMAC-signed provenance receipts, the open ledger at
              /treasury, and the fact that 51% of every mint is locked in
              wallets with no authority path. Verify, don't trust.
            </Faq>
            <Faq q="What if I lose my password?">
              File a reset request from the Unlock page. The Architect sees
              it in their inbox and approves it with a one-time temp
              password. Delivery is out-of-band (text/voice) — never email —
              so the credential can't be phished.
            </Faq>
            <Faq q="Is this open source?">
              Not currently — the Architect maintains the codebase directly.
              The API is public and documented, though; anything the app
              does, an external tool can also do.
            </Faq>
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="text-center mt-10 border-t border-zinc-900 pt-8">
          <p className="text-zinc-500 text-sm mb-4">
            The best way to learn it is to try it.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link
              to="/console"
              data-testid="how-to-console"
              className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 text-[10px] uppercase tracking-[0.25em] transition-colors"
            >
              <Activity className="h-3 w-3" /> Open the Console
            </Link>
            <Link
              to="/obsidian"
              data-testid="how-to-obsidian"
              className="inline-flex items-center gap-2 px-4 py-2 border border-fuchsia-500 text-fuchsia-300 hover:bg-fuchsia-500 hover:text-zinc-950 text-[10px] uppercase tracking-[0.25em] transition-colors"
            >
              <Eye className="h-3 w-3" /> Run a deep-dive
            </Link>
            <Link
              to="/treasury"
              data-testid="how-to-treasury"
              className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[10px] uppercase tracking-[0.25em] transition-colors"
            >
              <Coins className="h-3 w-3" /> See the treasury
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
